import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Cake, Building2, Home, Send, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KINDS = [
  { v: "birthday", l: "Kindergeburtstag", icon: Cake, hint: "Bis 20 Kids, mit Popcorn-Paket & Torten-Aufsteller." },
  { v: "corporate", l: "Firmen-Event", icon: Building2, hint: "Team-Kino, Präsentation oder Weihnachtsfeier." },
  { v: "private", l: "Kino privat mieten", icon: Home, hint: "Ganzer Saal exklusiv für deine Runde." },
];

export default function EventBookingBlock() {
  const [kind, setKind] = useState("birthday");
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    guest_count: 10,
    preferred_date: "", preferred_time: "",
    movie_wish: "", snack_wish: "", budget: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Bitte Name und E-Mail angeben.");
      return;
    }
    setBusy(true);
    try {
      const pin = localStorage.getItem("mh_landing_pin") || "";
      await axios.post(`${API}/events/booking`,
        { kind, ...form, guest_count: parseInt(form.guest_count) || 1 },
        { headers: pin ? { "X-Landing-Pin": pin } : {} },
      );
      setDone(true);
      toast.success("Anfrage gesendet — wir melden uns!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Konnte Anfrage nicht senden.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="mt-10 card-lux p-10 text-center" data-testid="event-booking-success">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#8FBC8F]/15 flex items-center justify-center mb-4">
          <CheckCircle2 size={28} className="text-[#8FBC8F]" />
        </div>
        <h3 className="font-heading text-3xl mb-2">Danke, {form.name.split(" ")[0]}!</h3>
        <p className="text-sm text-[#A8A398] max-w-md mx-auto">
          Deine Anfrage ist bei uns eingegangen. Wir melden uns innerhalb von 24 Stunden per E-Mail an <b className="text-[#EEDC82]">{form.email}</b> — mit Termin-Vorschlag und Angebot.
        </p>
        <button onClick={() => { setDone(false); setForm({ ...form, movie_wish: "", snack_wish: "", message: "" }); }}
          data-testid="event-booking-new"
          className="mt-6 text-[10px] uppercase tracking-widest text-[#D4AF37] hover:text-[#EEDC82]">
          Neue Anfrage stellen
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-10 card-lux p-8 space-y-6" data-testid="event-booking-form">
      {/* Event-Art */}
      <div className="grid sm:grid-cols-3 gap-3">
        {KINDS.map((k) => {
          const Icon = k.icon;
          const active = kind === k.v;
          return (
            <button type="button" key={k.v} onClick={() => setKind(k.v)}
              data-testid={`event-kind-${k.v}`}
              className={`text-left p-4 rounded border transitions-safe ${
                active ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A] hover:border-[#575148]"
              }`}>
              <Icon size={20} className={active ? "text-[#D4AF37]" : "text-[#A8A398]"} />
              <div className={`mt-2 text-sm font-medium ${active ? "text-[#F5F0E6]" : "text-[#F5F0E6]"}`}>{k.l}</div>
              <div className="text-[10px] text-[#575148] mt-1">{k.hint}</div>
            </button>
          );
        })}
      </div>

      {/* Kontakt */}
      <div className="grid md:grid-cols-2 gap-3">
        <Input label="Dein Name" value={form.name} onChange={upd("name")} testid="event-name" required />
        <Input label="E-Mail" type="email" value={form.email} onChange={upd("email")} testid="event-email" required />
        <Input label="Telefon (optional)" value={form.phone} onChange={upd("phone")} testid="event-phone" />
        <Input label="Anzahl Gäste" type="number" min="1" value={form.guest_count} onChange={upd("guest_count")} testid="event-guest-count" />
        <Input label="Wunschtermin" type="date" value={form.preferred_date} onChange={upd("preferred_date")} testid="event-date" />
        <Input label="Uhrzeit" type="time" value={form.preferred_time} onChange={upd("preferred_time")} testid="event-time" />
      </div>

      {/* Wünsche */}
      <div className="grid md:grid-cols-2 gap-3">
        <Input label="Film-Wunsch" value={form.movie_wish} onChange={upd("movie_wish")}
          placeholder={kind === "birthday" ? "z. B. Paddington 3" : "z. B. Barbie, Oppenheimer …"} testid="event-movie" />
        <Input label="Snack- oder Torten-Wünsche" value={form.snack_wish} onChange={upd("snack_wish")}
          placeholder="Popcorn, Nachos, Torte …" testid="event-snack" />
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Nachricht (optional)</label>
        <textarea rows={3} value={form.message} onChange={upd("message")}
          data-testid="event-message"
          placeholder="Was sollen wir für dein Event vorbereiten?"
          className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" />
      </div>

      <button type="submit" disabled={busy}
        data-testid="event-booking-submit"
        className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black rounded-full text-xs uppercase tracking-[0.4em] hover:bg-[#EEDC82] transition-colors disabled:opacity-40">
        {busy ? <><Loader2 size={14} className="animate-spin" /> Sende …</> : <><Send size={14} /> Anfrage senden</>}
      </button>
      <div className="text-[10px] text-[#575148]">Kein Vertrag, kein Risiko — wir bestätigen erst nach persönlicher Absprache.</div>
    </form>
  );
}

function Input({ label, testid, ...rest }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-1">{label}</div>
      <input {...rest} data-testid={testid}
        className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" />
    </label>
  );
}
